import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const MAXIMUM_ATTACHMENT_COUNT = 10;
const MAXIMUM_FILE_SIZE_IN_MEGABYTES = 100;
const MAXIMUM_FILE_SIZE_IN_BYTES = MAXIMUM_FILE_SIZE_IN_MEGABYTES * 1024 * 1024;

export const useGetChatInputAttachmentSelectionError = () => {
  const { t } = useTranslation();

  return useCallback(
    ({ currentAttachmentCount, files }: { currentAttachmentCount: number; files: File[] }) => {
      const oversizedFiles = files.filter((file) => file.size > MAXIMUM_FILE_SIZE_IN_BYTES);
      const hasExceededAttachmentCount = currentAttachmentCount + files.length > MAXIMUM_ATTACHMENT_COUNT;
      const [oversizedFile] = oversizedFiles;

      if (hasExceededAttachmentCount && oversizedFiles.length > 0) {
        return t('Nothing was added: max {{count}} attachments, max {{fileSize}} MB per file.', {
          count: MAXIMUM_ATTACHMENT_COUNT,
          fileSize: MAXIMUM_FILE_SIZE_IN_MEGABYTES,
        });
      }

      if (oversizedFiles.length === 1 && oversizedFile) {
        return t('{{fileName}} exceeds the {{fileSize}} MB limit. Nothing was added.', {
          fileName: oversizedFile.name,
          fileSize: MAXIMUM_FILE_SIZE_IN_MEGABYTES,
        });
      }

      if (oversizedFiles.length > 1) {
        return t('{{count}} files exceed the {{fileSize}} MB limit. Nothing was added.', {
          count: oversizedFiles.length,
          fileSize: MAXIMUM_FILE_SIZE_IN_MEGABYTES,
        });
      }

      if (hasExceededAttachmentCount) {
        return t('Maximum {{count}} attachments per message. Nothing was added.', {
          count: MAXIMUM_ATTACHMENT_COUNT,
        });
      }

      return undefined;
    },
    [t],
  );
};
